import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api, formatApiError } from '../lib/api';
import { t } from '../lib/i18n';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';

// Iter 122 — Auth surface simplified into 3 audiences:
//   * 'universal'    — /sign-in (any credential lands on their dashboard).
//                      /login and /staff-login now redirect here (see PublicRoutes.jsx).
//   * 'admin'        — /admin-login (rejects non-admin logins)
//   * 'patient-only' — /patient-login (rejects staff/admin logins so a patient
//                      link on the landing/booking flow can't accidentally sign
//                      a clinician into the patient portal)
export function Login() { return <LoginCore audience="universal" />; }
export function AdminLogin() { return <LoginCore audience="admin" />; }
export function PatientLogin() { return <LoginCore audience="patient-only" />; }

function LoginCore({ audience }) {
  const { setUser, locale } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from?.pathname;
  const [email, setEmail] = useState(audience === 'admin' ? 'admin@physioflow.eu' : '');
  const [password, setPassword] = useState(audience === 'admin' ? 'Admin123!' : '');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [needsReactivate, setNeedsReactivate] = useState(false);

  const doLogin = async (em, pw, code, reactivate) => {
    setErr(''); setLoading(true);
    try {
      const body = { email: em, password: pw };
      if (code) body.totp_code = code;
      if (reactivate) body.reactivate = true;
      const { data } = await api.post('/auth/login', body);
      setNeedsReactivate(false);
      // Audience guard — only enforced on portal-specific routes.
      // Universal (/sign-in, /login) accepts all roles and routes by role.
      if (audience === 'admin' && data.role !== 'admin') {
        setErr('Administrator access only.');
        setLoading(false); return;
      }
      if (audience === 'patient-only' && data.role !== 'patient') {
        setErr('This sign-in is for patients only. Staff and administrators please use the main Sign in page.');
        setLoading(false); return;
      }
      setUser(data);
      // Honour the original deep-link the user tried to reach (e.g. /patient/book from a welcome email).
      // Falls back to the role-default landing page.
      // Iter 106 — platform admins land on /platform (the SaaS operator hub).
      const isStaff = data.role === 'admin' || data.role === 'clinician' || data.role === 'reception';
      let dest = data.role === 'platform_admin' ? '/platform'
               : data.role === 'patient' ? '/patient'
               : '/admin';
      if (fromPath) {
        const wantsPlatform = fromPath.startsWith('/platform');
        const wantsStaff = fromPath.startsWith('/admin');
        const wantsPatient = fromPath.startsWith('/patient');
        if (data.role === 'platform_admin' && wantsPlatform) dest = fromPath;
        else if ((isStaff && wantsStaff) || (!isStaff && wantsPatient)) dest = fromPath;
      }
      navigate(dest);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const authStep = e.response?.headers?.['x-auth-step'];
      if (authStep === '2fa-required' || (detail && /2FA code required/i.test(detail))) {
        setNeedsTotp(true);
        setErr('Enter the 6-digit code from your authenticator app.');
      } else if (authStep === 'reactivate-required') {
        setNeedsReactivate(true);
        setErr('This account is paused (deactivated). Reactivate it to sign back in.');
      } else {
        setErr(formatApiError(detail) || e.message);
      }
    } finally { setLoading(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    doLogin(email, password, needsTotp ? totpCode : undefined);
  };

  const quickLogin = (em, pw) => {
    setEmail(em); setPassword(pw);
    setNeedsTotp(false); setTotpCode('');
    doLogin(em, pw);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const subtitle =
    audience === 'admin' ? 'Administrator access · Physiogenic' :
    audience === 'patient-only' ? 'Patient sign in · Physiogenic' :
                           'Sign in to Physiogenic.';

  return (
    <AuthShell>
      <h1 className="text-3xl font-heading font-bold tracking-tight">{t(locale, 'auth_login')}</h1>
      <p className="text-sm text-fg-muted mt-2">{subtitle}</p>

      {audience === 'patient-only' && (
        <>
          <div className="mt-6">
            <button type="button" onClick={() => quickLogin('patient@physioflow.eu', 'Patient123!')}
                    className="surface border border-soft rounded-lg p-3 text-left hover:border-[#003D80] transition w-full"
                    data-testid="quick-patient">
              <div className="text-[10px] uppercase tracking-wider text-accent font-medium">DEMO</div>
              <div className="font-heading font-semibold text-sm mt-0.5">Sign in as Patient</div>
              <div className="text-[11px] text-fg-muted">patient@physioflow.eu</div>
            </button>
          </div>
          <Divider label={t(locale, 'auth_or')} />
          <button type="button" onClick={googleLogin}
                  className="btn-secondary w-full inline-flex items-center justify-center gap-2"
                  data-testid="google-login-top">
            <GoogleGlyph /> {t(locale, 'auth_google') || 'Continue with Google'}
          </button>
          <Divider label={t(locale, 'auth_or')} />
        </>
      )}

      <form onSubmit={submit} className="space-y-4">
        <Field label={t(locale, 'auth_email')}>
          <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                 className="input" data-testid="login-email" />
        </Field>
        <Field label={t(locale, 'auth_password')}>
          <div className="relative">
            <input type={showPw ? 'text' : 'password'} required value={password} onChange={(e)=>setPassword(e.target.value)}
                   className="input pr-10" data-testid="login-password" />
            <button type="button" onClick={()=>setShowPw(v=>!v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg" data-testid="toggle-pw">
              {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
            </button>
          </div>
        </Field>
        {needsTotp && (
          <Field label="2FA code (6 digits)">
            <input required autoFocus inputMode="numeric" maxLength={6}
                   value={totpCode}
                   onChange={(e)=>setTotpCode(e.target.value.replace(/\D/g, '').slice(0,6))}
                   className="input font-mono text-center tracking-[0.4em] text-lg"
                   placeholder="000000" data-testid="login-totp" />
          </Field>
        )}
        {err && <div className="text-sm text-[#B83A3A]" data-testid="login-error">{err}</div>}
        {needsReactivate && (
          <button type="button" disabled={loading}
                  onClick={() => doLogin(email, password, needsTotp ? totpCode : undefined, true)}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                  data-testid="reactivate-login-btn">
            Reactivate my account & sign in
          </button>
        )}
        <button type="submit" disabled={loading}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="login-submit">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {t(locale, 'auth_login')}
        </button>
        <div className="text-center text-xs">
          <Link to="/forgot-password" className="text-fg-muted hover:text-brand" data-testid="forgot-pw-link">
            Forgot password?
          </Link>
        </div>
      </form>
      {audience === 'patient-only' ? (
        <div className="mt-6 text-sm text-fg-muted">
          {t(locale, 'auth_no_account')}{' '}
          <Link to="/register" className="text-brand font-medium" data-testid="goto-register">
            {t(locale, 'auth_register')}
          </Link>
          <div className="mt-3 pt-3 border-t border-soft text-xs">
            <Link to="/sign-in" className="text-fg-muted hover:text-brand inline-flex items-center gap-1" data-testid="staff-signin-link">
              Staff or administrator? Sign in here →
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-6 text-xs text-fg-muted text-center">
          Patient? <Link to="/patient-login" className="text-brand" data-testid="patient-signin-link">Patient sign in →</Link>
        </div>
      )}
    </AuthShell>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 18 18" className="w-4 h-4" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.92v2.32A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.96H.92A9 9 0 0 0 0 9c0 1.45.35 2.82.92 4.04l3.05-2.32z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0a9 9 0 0 0-8.08 4.96l3.05 2.32C4.68 5.16 6.66 3.58 9 3.58z"/>
    </svg>
  );
}

export function Register() {
  const { setUser, locale } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const urlParams = new URLSearchParams(useLocation().search);
  const [name, setName] = useState(urlParams.get('name') || '');
  const [email, setEmail] = useState(urlParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [invite, setInvite] = useState(params.code || '');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  // Personalized-booking summary — read the wizard answers if the visitor
  // came from the 3-step landing wizard. We persist them to /wizard/convert
  // on successful signup so the clinician has rich prep context on day one.
  const [wizard] = useState(() => {
    try {
      const raw = sessionStorage.getItem('wizard_answers');
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  });

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/register', {
        email, password, name, locale,
        invite_code: invite || undefined,
      });
      setUser(data);
      // Best-effort — never block redirect if it fails.
      if (wizard) {
        try {
          await api.post('/wizard/convert', wizard);
          sessionStorage.removeItem('wizard_answers');
        } catch (_) { /* ignore */ }
      }
      navigate(data.role === 'admin' ? '/admin' : '/patient');
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Persist the invite code (if any) to localStorage so AuthCallback can apply it after Google returns.
    if (invite) {
      try { localStorage.setItem('pending_invite_code', invite.toUpperCase()); } catch (_) {}
    }
    const redirectUrl = window.location.origin + '/auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell>
      <h1 className="text-3xl font-heading font-bold tracking-tight">{t(locale, 'auth_register')}</h1>
      <p className="text-sm text-fg-muted mt-2">Create your patient or clinic account.</p>

      {/* Personalized booking summary — shown when the visitor came from the
          landing wizard. Confirms their answers + sets expectation that the
          clinic already knows what they're booking for. */}
      {wizard && (
        <div className="mt-5 surface border border-brand/30 bg-brand/5 rounded-lg p-4" data-testid="wizard-summary">
          <div className="text-[11px] uppercase tracking-[0.2em] text-accent font-semibold mb-2">YOUR BOOKING SO FAR</div>
          <ul className="text-sm space-y-1.5">
            {wizard.complaint && (
              <li className="flex items-start gap-2">
                <span className="text-fg-muted text-xs uppercase mt-0.5 w-20 shrink-0">Concern</span>
                <span className="text-fg flex-1">{wizard.complaint}</span>
              </li>
            )}
            {wizard.discipline && wizard.discipline !== 'unsure' && (
              <li className="flex items-start gap-2">
                <span className="text-fg-muted text-xs uppercase mt-0.5 w-20 shrink-0">Service</span>
                <span className="text-fg flex-1 capitalize">{wizard.discipline}</span>
              </li>
            )}
            {wizard.timeline && (
              <li className="flex items-start gap-2">
                <span className="text-fg-muted text-xs uppercase mt-0.5 w-20 shrink-0">Timing</span>
                <span className="text-fg flex-1">{({this_week:'This week', soon:'Next 2–4 weeks', flexible:'Whenever'})[wizard.timeline] || wizard.timeline}</span>
              </li>
            )}
          </ul>
          <p className="text-[11px] text-fg-muted mt-3 italic">Your clinician will see this on your chart so the first session can focus on what matters most to you.</p>
        </div>
      )}

      <div className="mt-6">
        <button type="button" onClick={googleSignup}
                className="btn-secondary w-full inline-flex items-center justify-center gap-2"
                data-testid="google-signup-top">
          <GoogleGlyph /> {t(locale, 'auth_google_signup') || 'Sign up with Google'}
        </button>
      </div>
      <Divider label={t(locale, 'auth_or')} />

      <form onSubmit={submit} className="space-y-4">
        <Field label={t(locale, 'auth_name')}>
          <input required value={name} onChange={(e)=>setName(e.target.value)} className="input" data-testid="register-name" />
        </Field>
        <Field label={t(locale, 'auth_email')}>
          <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="input" data-testid="register-email" />
        </Field>
        <Field label={t(locale, 'auth_password')}>
          <input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="input" data-testid="register-password" />
        </Field>
        <Field label={t(locale, 'auth_invite')}>
          <input value={invite} onChange={(e)=>setInvite(e.target.value.toUpperCase())} className="input font-mono tracking-[0.2em]" placeholder="ABC123" data-testid="register-invite" />
        </Field>
        {err && <div className="text-sm text-[#B83A3A]" data-testid="register-error">{err}</div>}
        <button type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="register-submit">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {t(locale, 'auth_register')}
        </button>
      </form>
      <div className="mt-6 text-sm text-fg-muted">
        {t(locale, 'auth_have_account')} <Link to="/login" className="text-brand font-medium" data-testid="goto-login">{t(locale, 'auth_login')}</Link>
      </div>
    </AuthShell>
  );
}

export function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth, setUser } = useAuth();
  const [err, setErr] = useState('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // useRef prevents StrictMode double-invoke from consuming the one-time session_id twice.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { navigate('/login'); return; }
    const sessionId = m[1];
    (async () => {
      try {
        let inviteCode = null;
        try {
          inviteCode = localStorage.getItem('pending_invite_code') || null;
          if (inviteCode) localStorage.removeItem('pending_invite_code');
        } catch (_) {}
        const { data } = await api.post('/auth/google/session', {
          session_id: sessionId,
          invite_code: inviteCode || undefined,
        });
        // Clean the session_id out of the URL fragment immediately (one-time use).
        if (window.history?.replaceState) {
          window.history.replaceState(null, '', window.location.pathname);
        }
        if (data?.user_id) setUser(data);
        else await checkAuth();
        const landing = data?.role === 'patient' ? '/patient' : '/admin';
        navigate(landing, { replace: true });
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
        setTimeout(() => navigate('/login'), 1500);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center section">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand mx-auto" />
        <div className="mt-4 text-sm text-fg-muted">{err || 'Signing you in...'}</div>
      </div>
    </div>
  );
}

function AuthShell({ children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2 section">
      <div className="hidden md:flex flex-col justify-between p-12 surface border-r border-soft">
        <Link to="/" className="flex items-center gap-2.5">
          <BrandLogo size={68} showCopyright/>
          <span className="font-heading font-bold">Physiogenic</span>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-3">EU MEDTECH</div>
          <h2 className="text-4xl font-heading font-bold tracking-tight max-w-md leading-tight">
            Treatment plans, not paperwork.
          </h2>
          <p className="mt-4 text-fg-muted max-w-md">
            HIPAA and GDPR compliance, multilingual UI, and AI-assisted reporting.
          </p>
        </div>
        <div className="text-xs text-fg-muted">© {new Date().getFullYear()} Physiogenic</div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-fg-secondary mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function Divider({ label }) {
  return (
    <div className="my-6 flex items-center gap-3 text-xs text-fg-muted">
      <div className="flex-1 h-px bg-[var(--border)]" />
      {label}
      <div className="flex-1 h-px bg-[var(--border)]" />
    </div>
  );
}

export function AcceptInvite() {
  const { code } = useParams();
  const { user, locale } = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/invites/code/${code}`).then(({ data }) => setInvite(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, [code]);

  return (
    <AuthShell>
      <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-3">INVITATION</div>
      <h1 className="text-3xl font-heading font-bold tracking-tight">You're invited</h1>
      <p className="text-sm text-fg-muted mt-2">Use code <span className="font-mono tracking-[0.2em] text-fg">{code}</span> to join the clinic.</p>
      {err && <div className="mt-6 text-sm text-[#B83A3A]" data-testid="invite-error">{err}</div>}
      {invite && (
        <div className="mt-6 surface border border-soft rounded-xl p-4">
          <div className="text-xs text-fg-muted">Role</div>
          <div className="font-medium text-fg uppercase tracking-wider text-sm">{invite.role}</div>
        </div>
      )}
      <div className="mt-8 flex gap-3">
        <Link to={`/register/${code}`} className="btn-primary" data-testid="invite-register">{t(locale, 'auth_register')}</Link>
        <Link to="/login" className="btn-secondary">{t(locale, 'auth_login')}</Link>
      </div>
    </AuthShell>
  );
}
